/**
 * Sales Orders list attention from inbound / Operational Issue authority.
 * Read-model only: does not write providers, refunds, inventory, or fulfilment.
 */
import { buildInboundAfterSalesAttention } from "./order-attention";
import { ebayInboundIssueSpec, ebayInboundStateIsClosed } from "./inbound-after-sales";
import { ebayInboundCancelIsCompleted } from "./inbound-buyer-cancel";
import { ACTIVE_ISSUE_STATUSES, exceptionTypeLabel } from "./operational-issues";
import { providerDisplayName } from "./order-provenance";

export const SALES_ORDER_LIST_AFTER_SALES_KINDS = [
  "buyer_cancellation",
  "buyer_return",
  "buyer_inr",
  "buyer_snad",
  "ebay_case",
] as const;

export type SalesOrderListAfterSalesKind = (typeof SALES_ORDER_LIST_AFTER_SALES_KINDS)[number];

const KIND_PRIORITY: Record<SalesOrderListAfterSalesKind, number> = {
  buyer_cancellation: 0,
  buyer_return: 1,
  buyer_snad: 2,
  buyer_inr: 3,
  ebay_case: 4,
};

export type SalesOrderListInboundEvent = {
  sales_order_id: string;
  connector_id?: string | null;
  request_kind: string;
  provider_request_id: string;
  initiator?: string | null;
  reason?: string | null;
  request_state?: string | null;
  provider_order_cancel_state?: string | null;
  provider_payment_status?: string | null;
  provider_fulfillment_status?: string | null;
};

export type SalesOrderListOrderContext = {
  id: string;
  status?: string | null;
  paymentStatus?: string | null;
  provider?: string | null;
};

export type SalesOrderListAfterSalesTone = "actionable" | "info";

export type SalesOrderListOperationalIssue = {
  entity_ref: string | null;
  exception_type: string;
  status: string;
  connector_id?: string | null;
};

export type SalesOrderListAfterSalesBadge = {
  salesOrderId: string;
  kind: SalesOrderListAfterSalesKind;
  tone: SalesOrderListAfterSalesTone;
  label: string;
  href: string;
  extraCount: number;
};

export const SALES_ORDER_LIST_AFTER_SALES_EXCEPTION_TYPES = SALES_ORDER_LIST_AFTER_SALES_KINDS.map(
  (kind) => ebayInboundIssueSpec(kind).exceptionType,
);

export function isSalesOrderListAfterSalesKind(kind: string): kind is SalesOrderListAfterSalesKind {
  return (SALES_ORDER_LIST_AFTER_SALES_KINDS as readonly string[]).includes(kind);
}

export function salesOrderListAfterSalesHref(salesOrderId: string, tone: SalesOrderListAfterSalesTone = "actionable"): string {
  return tone === "actionable"
    ? `/sales-orders/${salesOrderId}#order-attention`
    : `/sales-orders/${salesOrderId}`;
}

function upper(value: unknown): string {
  return String(value ?? "").trim().toUpperCase();
}

function channelPrefix(provider: string | null | undefined): string {
  return providerDisplayName(provider) || "eBay";
}

function tokenLooksRefunded(value: unknown): boolean {
  const raw = upper(value);
  if (!raw) return false;
  if (raw.includes("NO_REFUND")) return false;
  return (
    raw.includes("WITH_REFUND")
    || raw.includes("FULLY_REFUNDED")
    || raw.includes("PARTIALLY_REFUNDED")
    || raw.endsWith("_REFUNDED")
    || raw === "REFUNDED"
  );
}

function inboundHasProviderRefund(event: SalesOrderListInboundEvent): boolean {
  return (
    tokenLooksRefunded(event.request_state)
    || tokenLooksRefunded(event.provider_order_cancel_state)
    || tokenLooksRefunded(event.provider_payment_status)
  );
}

function inboundIsClosed(event: SalesOrderListInboundEvent): boolean {
  if (event.request_kind === "buyer_cancellation") {
    return ebayInboundCancelIsCompleted({
      requestState: event.request_state,
      orderCancelState: event.provider_order_cancel_state,
    });
  }
  return ebayInboundStateIsClosed(event.request_state);
}

function canonicalAlreadyShowsResolved(opts: {
  kind: SalesOrderListAfterSalesKind;
  refunded: boolean;
  tradivoStatus?: string | null;
  tradivoPaymentStatus?: string | null;
}): boolean {
  const status = (opts.tradivoStatus ?? "").trim();
  const payment = (opts.tradivoPaymentStatus ?? "").trim();
  if (opts.kind === "buyer_return" || opts.kind === "buyer_snad") {
    return status === "returned" || status === "partially_returned";
  }
  if (opts.kind === "buyer_cancellation") {
    if (opts.refunded) return payment === "refunded" || payment === "partially_refunded";
    return status === "cancelled";
  }
  return false;
}

function resolvedMarketplaceLabel(opts: {
  kind: SalesOrderListAfterSalesKind;
  refunded: boolean;
  provider?: string | null;
}): string | null {
  const channel = channelPrefix(opts.provider);
  if (opts.kind === "buyer_cancellation") {
    return opts.refunded ? `${channel}: Cancelled & refunded` : `${channel}: Cancellation completed`;
  }
  if (opts.kind === "buyer_return" || opts.kind === "buyer_snad") {
    return `${channel}: Return closed`;
  }
  if (opts.kind === "ebay_case" || opts.kind === "buyer_inr") return `${channel}: Case closed`;
  return null;
}

function kindFromExceptionType(exceptionType: string): SalesOrderListAfterSalesKind | null {
  for (const kind of SALES_ORDER_LIST_AFTER_SALES_KINDS) {
    if (ebayInboundIssueSpec(kind).exceptionType === exceptionType) return kind;
  }
  return null;
}

function inboundAttention(event: SalesOrderListInboundEvent) {
  if (!isSalesOrderListAfterSalesKind(event.request_kind)) return null;
  return buildInboundAfterSalesAttention({
    requestKind: event.request_kind,
    providerRequestId: event.provider_request_id,
    initiator: event.initiator,
    reason: event.reason,
    requestState: event.request_state,
    providerOrderCancelState: event.provider_order_cancel_state,
    providerFulfillmentStatus: event.provider_fulfillment_status,
  });
}

export function salesOrderListAfterSalesBadgeForOrder(opts: {
  salesOrderId: string;
  inboundEvents: readonly SalesOrderListInboundEvent[];
  operationalIssues?: readonly SalesOrderListOperationalIssue[];
  order?: SalesOrderListOrderContext | null;
}): SalesOrderListAfterSalesBadge | null {
  type Item = { kind: SalesOrderListAfterSalesKind; label: string; tone: SalesOrderListAfterSalesTone };
  const items: Item[] = [];
  const seenActionable = new Set<SalesOrderListAfterSalesKind>();
  const inboundForOrder = opts.inboundEvents.filter((event) => event.sales_order_id === opts.salesOrderId);

  for (const event of inboundForOrder) {
    const attention = inboundAttention(event);
    if (!attention || !isSalesOrderListAfterSalesKind(attention.kind)) continue;
    if (seenActionable.has(attention.kind)) continue;
    seenActionable.add(attention.kind);
    items.push({ kind: attention.kind, label: attention.title, tone: "actionable" });
  }

  for (const issue of opts.operationalIssues ?? []) {
    if (issue.entity_ref !== opts.salesOrderId) continue;
    if (!(ACTIVE_ISSUE_STATUSES as readonly string[]).includes(issue.status)) continue;
    const kind = kindFromExceptionType(issue.exception_type);
    if (!kind || seenActionable.has(kind)) continue;
    const matchingInbound = inboundForOrder.filter((event) => event.request_kind === kind);
    if (matchingInbound.length > 0 && matchingInbound.every((event) => inboundAttention(event) == null)) {
      continue;
    }
    seenActionable.add(kind);
    items.push({ kind, label: exceptionTypeLabel(issue.exception_type), tone: "actionable" });
  }

  items.sort((a, b) => KIND_PRIORITY[a.kind] - KIND_PRIORITY[b.kind]);
  if (items.length > 0) {
    const extraCount = items.length - 1;
    const primary = items[0];
    return {
      salesOrderId: opts.salesOrderId,
      kind: primary.kind,
      tone: "actionable",
      label: extraCount > 0 ? `${primary.label} +${extraCount}` : primary.label,
      href: salesOrderListAfterSalesHref(opts.salesOrderId, "actionable"),
      extraCount,
    };
  }

  const seenResolved = new Set<SalesOrderListAfterSalesKind>();
  const resolved: Item[] = [];
  for (const event of inboundForOrder) {
    if (!isSalesOrderListAfterSalesKind(event.request_kind)) continue;
    if (!inboundIsClosed(event)) continue;
    if (seenResolved.has(event.request_kind)) continue;
    const refunded = inboundHasProviderRefund(event);
    if (
      canonicalAlreadyShowsResolved({
        kind: event.request_kind,
        refunded,
        tradivoStatus: opts.order?.status,
        tradivoPaymentStatus: opts.order?.paymentStatus,
      })
    ) {
      continue;
    }
    const label = resolvedMarketplaceLabel({
      kind: event.request_kind,
      refunded,
      provider: opts.order?.provider,
    });
    if (!label) continue;
    seenResolved.add(event.request_kind);
    resolved.push({ kind: event.request_kind, label, tone: "info" });
  }

  resolved.sort((a, b) => KIND_PRIORITY[a.kind] - KIND_PRIORITY[b.kind]);
  if (resolved.length === 0) return null;
  const extraCount = resolved.length - 1;
  const primary = resolved[0];
  return {
    salesOrderId: opts.salesOrderId,
    kind: primary.kind,
    tone: "info",
    label: extraCount > 0 ? `${primary.label} +${extraCount}` : primary.label,
    href: salesOrderListAfterSalesHref(opts.salesOrderId, "info"),
    extraCount,
  };
}

export function indexSalesOrderListAfterSalesBadges(opts: {
  inboundEvents: readonly SalesOrderListInboundEvent[];
  operationalIssues?: readonly SalesOrderListOperationalIssue[];
  orders?: readonly SalesOrderListOrderContext[];
}): Record<string, SalesOrderListAfterSalesBadge> {
  const ids = new Set<string>();
  const orderById = new Map((opts.orders ?? []).map((order) => [order.id, order]));
  for (const event of opts.inboundEvents) {
    if (event.sales_order_id) ids.add(event.sales_order_id);
  }
  for (const issue of opts.operationalIssues ?? []) {
    if (issue.entity_ref) ids.add(issue.entity_ref);
  }
  for (const order of opts.orders ?? []) ids.add(order.id);
  const out: Record<string, SalesOrderListAfterSalesBadge> = {};
  for (const salesOrderId of ids) {
    const badge = salesOrderListAfterSalesBadgeForOrder({
      salesOrderId,
      inboundEvents: opts.inboundEvents,
      operationalIssues: opts.operationalIssues,
      order: orderById.get(salesOrderId) ?? null,
    });
    if (badge) out[salesOrderId] = badge;
  }
  return out;
}
